import { useState, useEffect, type MouseEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Search, Printer, Send, ChevronRight } from 'lucide-react'
import { service } from '../lib/api/service'
import { entryToATA } from '../lib/api/transform'
import type { Project, ATAView } from '../lib/api/types'

export default function UnderrattelserScreen() {
  const navigate = useNavigate()

  const [project, setProject] = useState<Project | null>(null)
  const [atas, setAtas]       = useState<ATAView[]>([])
  const [loading, setLoading] = useState(true)
  const [tab, setTab]         = useState('skickade')
  const [search, setSearch]   = useState('')

  useEffect(() => {
    Promise.all([service.getProject(), service.getAllEntries(), service.getSurcharges()])
      .then(([proj, entries, sc]) => {
        setProject(proj)
        setAtas(entries.map(e => entryToATA(e, sc)))
      })
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="loading"><div className="spinner" /></div>

  const harU = atas.filter(a => a.harUnderrattelse)
  const invanar    = harU.filter(a => a.bestallarSvar === 'Inväntar besked')
  const besvarade  = harU.filter(a => a.bestallarSvar && a.bestallarSvar !== '–' && a.bestallarSvar !== 'Inväntar besked')
  const saknas     = atas.filter(a => !a.harUnderrattelse)

  const TABS = [
    { id: 'skickade',  label: 'Skickade',           count: harU.length,      warn: false },
    { id: 'invanar',   label: 'Inväntar svar',       count: invanar.length,   warn: invanar.length > 0 },
    { id: 'besvarade', label: 'Besvarade',           count: besvarade.length, warn: false },
    { id: 'saknas',    label: 'Saknas',              count: saknas.length,    warn: saknas.length > 0 },
  ]

  const tabData: Record<string, ATAView[]> = { skickade: harU, invanar, besvarade, saknas }
  const rows = tabData[tab] ?? []

  const filtered = rows.filter(a => {
    const q = search.toLowerCase()
    return !q ||
      String(a.sortOrder).includes(q) ||
      a.rubrik.toLowerCase().includes(q)
  })

  const projectLine = project
    ? [project.projektnr, project.projektNamn, project.ort].filter(Boolean).join(' · ')
    : null

  return (
    <div>
      <div className="page-header">
        <div>
          {projectLine && <div className="page-eyebrow">{projectLine}</div>}
          <h1 className="page-title">Underrättelser</h1>
        </div>
        <div className="page-actions">
          <button className="icon-btn" onClick={() => window.print()}>
            <Printer size={14} />
            Skriv ut
          </button>
        </div>
      </div>

      {/* Tab pills */}
      <div className="tab-pills">
        {TABS.map(t => (
          <button
            key={t.id}
            className={`tab-pill${tab === t.id ? ' active' : ''}`}
            onClick={() => setTab(t.id)}
          >
            {t.label}
            <span className={`tab-pill-count${t.warn ? ' warn' : ''}`}>{t.count}</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="view-toolbar" style={{ marginTop: 14 }}>
        <div className="search-input">
          <Search size={13} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Sök ÄTA…"
          />
        </div>
      </div>

      {/* Rows */}
      <div className="card" style={{ padding: 0, marginTop: 8 }}>
        {filtered.length === 0 ? (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--ink-faint)', fontSize: 13 }}>
            Inga ärenden i denna kategori.
          </div>
        ) : (
          filtered.map((a, i) => (
            <UnderrattelseRow
              key={a.sortOrder}
              ata={a}
              last={i === filtered.length - 1}
              onClick={() => navigate(`/ata/${a.sortOrder}`)}
              onPrint={e => { e.stopPropagation(); navigate(`/print/notification/${a.sortOrder}`) }}
            />
          ))
        )}
      </div>
    </div>
  )
}

interface UnderrattelseRowProps {
  ata: ATAView
  last: boolean
  onClick: () => void
  onPrint: (e: MouseEvent) => void
}

function UnderrattelseRow({ ata, last, onClick, onPrint }: UnderrattelseRowProps) {
  const skickad = ata.harUnderrattelse && ata.anmald

  return (
    <div
      className="u-row"
      style={{ borderBottom: last ? 'none' : '.5px solid var(--border)', cursor: 'pointer' }}
      onClick={onClick}
    >
      <span className="u-row-nr">ÄTA-{String(ata.sortOrder).padStart(3, '0')}</span>

      <span className="u-row-rubrik">{ata.rubrik || <span style={{ color: 'var(--ink-faint)' }}>(ingen rubrik)</span>}</span>

      <span className="u-row-status" style={{ color: skickad ? 'var(--ink-dim)' : 'oklch(0.55 0.13 55)' }}>
        {skickad ? (
          <><Send size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: 4 }} />Skickad</>
        ) : (
          'Ej skickad'
        )}
      </span>

      <span className="u-row-datum" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
        {ata.anmald ?? <span style={{ color: 'var(--ink-faint)' }}>–</span>}
      </span>

      <span className="u-row-svar">
        <SvarDot svar={ata.bestallarSvar} />
      </span>

      <span className="u-row-actions" onClick={e => e.stopPropagation()}>
        {ata.harUnderrattelse && (
          <button className="icon-button" title="Skriv ut brev" onClick={onPrint}>
            <Printer size={13} />
          </button>
        )}
        <ChevronRight size={14} style={{ color: 'var(--ink-faint)' }} />
      </span>
    </div>
  )
}

function SvarDot({ svar }: { svar: string }) {
  let color = 'var(--ink-faint)'
  if (svar === 'Inväntar besked') color = 'oklch(0.55 0.13 75)'
  else if (svar === 'Godkänd' || svar === 'Godkänt') color = 'oklch(0.55 0.12 145)'
  else if (svar === 'Avböjd' || svar === 'Avböjt') color = 'oklch(0.55 0.15 30)'

  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--ink-dim)' }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />
      {svar && svar !== '–' ? svar : <span style={{ color: 'var(--ink-faint)' }}>–</span>}
    </span>
  )
}
